import React, { useCallback, useState } from "react";
import "./NotificationsSettingsModal.scss";
import ToolTip from "../ui/common/ToolTip";
import { useAppDispatch } from "../../store";
import { updateSubscriptions } from "../../store/slices/currentUser";

type TabsTableNavigationProps = {
  algorithmCategories: Array<any>;
  activeCategory: string;
  setActiveCategory: Function;
};

type NotificationsSettingsModalProps = {
  algorithms: Array<any>;
  user: any;
  hasWoodwingConfig?: boolean;
};

type CategoryAlgorithm  = {
  id: string;
  type: string;
  name: string;
  title: string;
  filteringTags: string[];
};

const TabsTableNavigation = ({ algorithmCategories, activeCategory, setActiveCategory }: TabsTableNavigationProps) => {
  return (
    <div className="notification-settings-table-navigation">
      {algorithmCategories.map((e) => {
        return (
          <div
            className={[
              "notification-settings-table-nav-item",
              e.id === activeCategory ? "active" : null,
              e.items.length ? "disable" : null,
            ].join(" ")}
            onClick={() => {
              setActiveCategory(e);
            }}
            key={e.id}
          >
            {e.label}
            {e.items.length && <span className="notification-settings-table-badge">{e.items.length}</span>}
          </div>
        );
      })}
    </div>
  );
};

const NotificationsSettingsModal = ({ algorithms, user, hasWoodwingConfig = false }: NotificationsSettingsModalProps) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const dispatch = useAppDispatch();

  const setActiveAlgorithmCategory = (algorithmCategory) => {
    if (!algorithmCategory.disabled) {
      setActiveCategory(algorithmCategory.id);
    }
  };

  const isNotificationSubtype = (searchString: string, notificationTypes: Array<Record<string, string[]>>): boolean => {
    return notificationTypes.some(notificationObj => {
      const [type, subtypes] = Object.entries(notificationObj)[0];
      return subtypes.includes(searchString);
    });
  };

  const toggleNotificationSelection = (categoryAlgorithm: CategoryAlgorithm, subscriptionType: string) => {
    const subscriptionsData = JSON.parse(JSON.stringify(user.subscriptions));

    // Find if there's an existing object with this algorithm
    const existingIndex = subscriptionsData[subscriptionType].notificationTypes.findIndex(
      notifObj => Object.keys(notifObj)[0] === categoryAlgorithm.type
    );
    // If the algorithm is already in the list, remove it
    if (existingIndex === -1) {
      // If doesn't exist, add new object with algorithm and its subtypes
      const subtypes = subscriptionsData[subscriptionType].notificationTypes[categoryAlgorithm.type];
      subscriptionsData[subscriptionType].notificationTypes.push({
        [categoryAlgorithm.type]: Array.isArray(subtypes) ? subtypes : [categoryAlgorithm.name]
      });
    } else {
      // Get the current notification object
      const notificationObj = subscriptionsData[subscriptionType].notificationTypes[existingIndex];
      const subtypes = notificationObj[categoryAlgorithm.type];

      if (Array.isArray(subtypes)) {
        if (subtypes.includes(categoryAlgorithm.name)) {
          // Remove the name if it exists
          const newSubtypes = subtypes.filter(subtype => subtype !== categoryAlgorithm.name);

          if (newSubtypes.length === 0) {
            // If no subtypes left, remove the entire object
            subscriptionsData[subscriptionType].notificationTypes.splice(existingIndex, 1);
          } else {
            // Update with remaining subtypes
            subscriptionsData[subscriptionType].notificationTypes[existingIndex] = {
              [categoryAlgorithm.type]: newSubtypes
            };
          }
        } else {
          // Add the name if it doesn't exist
          subscriptionsData[subscriptionType].notificationTypes[existingIndex] = {
            [categoryAlgorithm.type]: [...subtypes, categoryAlgorithm.name]
          };
        }
      }
    }
    dispatch(updateSubscriptions(subscriptionsData));
  };

  const toggleBrandSelection = (brandId, subscriptionType) => {
    const subscriptionsData = JSON.parse(JSON.stringify(user.subscriptions));

    if (!subscriptionsData[subscriptionType].hasOwnProperty("brands")) {
      subscriptionsData[subscriptionType].brands = [];
    }

    if (subscriptionsData[subscriptionType].brands.indexOf(brandId) === -1) {
      subscriptionsData[subscriptionType].brands.push(brandId);
    } else {
      subscriptionsData[subscriptionType].brands.splice(subscriptionsData[subscriptionType].brands.indexOf(brandId), 1);
    }

    dispatch(updateSubscriptions(subscriptionsData));
  };

  const renderActiveCategoryAlgorithms = useCallback(() => {
    const activeCategoryAlgorithms = algorithms.find((algorithmCategory) => algorithmCategory.id === activeCategory);

    if (activeCategoryAlgorithms && activeCategoryAlgorithms.items) {
      return activeCategoryAlgorithms.items.map((categoryAlgorithm, index) => (
        <div className="notification-settings-table-item" key={categoryAlgorithm.id}>
          <div className="notification-settings-table-item-title">
            <p title={categoryAlgorithm.name}>{categoryAlgorithm.title}</p>
          </div>

          <div className="notification-settings-table-item-details">
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.mailNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${categoryAlgorithm.name}-mail`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.mailNotifications.active}
                  message="First, activate mail in the previous screen"
                >
                  Mail
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${categoryAlgorithm.name}-mail`}
                disabled={!user.subscriptions.mailNotifications.active}
                onChange={() => {
                  toggleNotificationSelection(categoryAlgorithm, "mailNotifications");
                }}
                checked={isNotificationSubtype(categoryAlgorithm.name, user.subscriptions.mailNotifications.notificationTypes)}
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.slackNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${categoryAlgorithm.name}-slack`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.slackNotifications.active}
                  message="First, configure and activate Slack in the previous screen"
                >
                  Slack
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${categoryAlgorithm.name}-slack`}
                disabled={!user.subscriptions.slackNotifications.active}
                onChange={() => {
                  toggleNotificationSelection(categoryAlgorithm, "slackNotifications");
                }}
                checked={isNotificationSubtype(categoryAlgorithm.name, user.subscriptions.slackNotifications.notificationTypes)}
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.googleChatNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${categoryAlgorithm.name}-gchat`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.googleChatNotifications.active}
                  message="First, configure and activate Google Chat in the previous screen"
                >
                  Google Chat
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${categoryAlgorithm.name}-gchat`}
                disabled={!user.subscriptions.googleChatNotifications.active}
                onChange={() => {
                  toggleNotificationSelection(categoryAlgorithm, "googleChatNotifications");
                }}
                checked={isNotificationSubtype(categoryAlgorithm.name, user.subscriptions.googleChatNotifications.notificationTypes)}
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.teamsNotifications?.active ? { color: "grey" } : {}}
                htmlFor={`${categoryAlgorithm.name}-msteams`}
              >
                <div className="tooltip">
                  <ToolTip
                    position={index !== 0 ? "top" : "bottom"}
                    disabled={user.subscriptions.teamsNotifications?.active}
                    message="First, configure and activate Microsoft Teams in the previous screen"
                  >
                    Microsoft Teams
                  </ToolTip>
                </div>
              </label>
              <input
                type="checkbox"
                id={`${categoryAlgorithm.name}-msteams`}
                disabled={!user.subscriptions.teamsNotifications?.active}
                onChange={() => {
                  toggleNotificationSelection(categoryAlgorithm, "teamsNotifications");
                }}
                checked={isNotificationSubtype(categoryAlgorithm.name, user.subscriptions.teamsNotifications.notificationTypes)}
              />
            </div>
            {hasWoodwingConfig && (
              <div className="notification-settings-table-detail">
                <label
                  style={!user.subscriptions.woodwingNotifications?.active ? { color: "grey" } : {}}
                  htmlFor={`${categoryAlgorithm.name}-woodwing`}
                >
                  <div className="tooltip">
                    <ToolTip
                      position={index !== 0 ? "top" : "bottom"}
                      disabled={user.subscriptions.woodwingNotifications?.active}
                      message="First, configure and activate Woodwing in the previous screen"
                    >
                      Woodwing
                    </ToolTip>
                  </div>
                </label>
                <input
                  type="checkbox"
                  id={`${categoryAlgorithm.name}-woodwing`}
                  disabled={!user.subscriptions.woodwingNotifications?.active}
                  onChange={() => {
                    toggleNotificationSelection(categoryAlgorithm, "woodwingNotifications");
                  }}
                  checked={isNotificationSubtype(categoryAlgorithm.name, user.subscriptions.woodwingNotifications.notificationTypes)}
                />
              </div>
            )}
          </div>
        </div>
      ));
    }

    return "";
  }, [algorithms, user, activeCategory]);

  const renderUserBrands = useCallback(() => {
    if (user.brands && user.brands.length) {
      return user.brands.map((brand, index) => (
        <div className="notification-settings-table-item" key={brand.id}>
          <div className="notification-settings-table-item-title">
            <p>{brand.name}</p>
          </div>

          <div className="notification-settings-table-item-details">
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.mailNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${brand.id}-mail`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.mailNotifications.active}
                  message="First, activate mail in the previous screen"
                >
                  Mail
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${brand.id}-mail`}
                disabled={!user.subscriptions.mailNotifications.active}
                onChange={() => {
                  toggleBrandSelection(brand.id, "mailNotifications");
                }}
                checked={
                  user.subscriptions.mailNotifications.brands &&
                  user.subscriptions.mailNotifications.brands.indexOf(brand.id) !== -1
                }
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.slackNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${brand.id}-slack`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.slackNotifications.active}
                  message="First, configure and activate Slack in the previous screen"
                >
                  Slack
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${brand.id}-slack`}
                disabled={!user.subscriptions.slackNotifications.active}
                onChange={() => {
                  toggleBrandSelection(brand.id, "slackNotifications");
                }}
                checked={
                  user.subscriptions.slackNotifications.brands &&
                  user.subscriptions.slackNotifications.brands.indexOf(brand.id) !== -1
                }
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.googleChatNotifications.active ? { color: "grey" } : {}}
                htmlFor={`${brand.id}-gchat`}
              >
                <ToolTip
                  position={index !== 0 ? "top" : "bottom"}
                  disabled={user.subscriptions.googleChatNotifications.active}
                  message="First, configure and activate Google Chat in the previous screen"
                >
                  Google Chat
                </ToolTip>
              </label>
              <input
                type="checkbox"
                id={`${brand.id}-gchat`}
                disabled={!user.subscriptions.googleChatNotifications.active}
                onChange={() => {
                  toggleBrandSelection(brand.id, "googleChatNotifications");
                }}
                checked={
                  user.subscriptions.googleChatNotifications.brands &&
                  user.subscriptions.googleChatNotifications.brands.indexOf(brand.id) !== -1
                }
              />
            </div>
            <div className="notification-settings-table-detail">
              <label
                style={!user.subscriptions.teamsNotifications?.active ? { color: "grey" } : {}}
                htmlFor={`${brand.id}-msteams`}
              >
                <div className="tooltip">
                  <ToolTip
                    position={index !== 0 ? "top" : "bottom"}
                    disabled={user.subscriptions.teamsNotifications?.active}
                    message="First, configure and activate Microsoft Teams in the previous screen"
                  >
                    Microsoft Teams
                  </ToolTip>
                </div>
              </label>
              <input
                type="checkbox"
                id={`${brand.id}-msteams`}
                disabled={!user.subscriptions.teamsNotifications?.active}
                onChange={() => {
                  toggleBrandSelection(brand.id, "teamsNotifications");
                }}
                checked={
                  user.subscriptions.teamsNotifications?.brands &&
                  user.subscriptions.teamsNotifications?.brands.indexOf(brand.id) !== -1
                }
              />
            </div>
            {hasWoodwingConfig && (
              <div className="notification-settings-table-detail">
                <label
                  style={!user.subscriptions.woodwingNotifications?.active ? { color: "grey" } : {}}
                  htmlFor={`${brand.id}-woodwing`}
                >
                  <div className="tooltip">
                    <ToolTip
                      position={index !== 0 ? "top" : "bottom"}
                      disabled={user.subscriptions.woodwingNotifications?.active}
                      message="First, configure and activate Wooding support in the previous screen"
                    >
                      Woodwing
                    </ToolTip>
                  </div>
                </label>
                <input
                  type="checkbox"
                  id={`${brand.id}-woodwing`}
                  disabled={!user.subscriptions.woodwingNotifications?.active}
                  onChange={() => {
                    toggleBrandSelection(brand.id, "woodwingNotifications");
                  }}
                  checked={
                    user.subscriptions.woodwingNotifications?.brands &&
                    user.subscriptions.woodwingNotifications?.brands.indexOf(brand.id) !== -1
                  }
                />
              </div>
            )}
          </div>
        </div>
      ));
    }

    return "";
  }, [user]);

  return (
    <div className="notification-settings">
      <h2>Which notifications do you want to receive?</h2>
      <div className="notification-settings-table">
        <TabsTableNavigation
          algorithmCategories={algorithms}
          activeCategory={activeCategory}
          setActiveCategory={setActiveAlgorithmCategory}
        />

        <div className="notification-settings-table-items">{renderActiveCategoryAlgorithms()}</div>
      </div>

      <h2>For which brands would you like to receive notifications?</h2>
      <div className="notification-settings-table">
        <div className="notification-settings-table-items">{renderUserBrands()}</div>
      </div>
    </div>
  );
};

export default NotificationsSettingsModal;
